import { z } from "zod";
import { isoDate, optionalText, text, uuid } from "./common";

/*
 * Patient engagement (Phase 5): message templates, consent, notifications (outbound messages), the
 * WhatsApp inbox, automation workflows, appointment reminders, recalls, follow-ups and staff tasks.
 *
 * Rules:
 *  - Every outbound message is a notification with an idempotency key: retries and redelivered
 *    events can never send the same reminder twice.
 *  - Consent is checked just before sending. With no recorded choice, service messages
 *    (transactional, reminders) are allowed and marketing is not.
 *  - WhatsApp without a connected number is not faked: the message waits in "To send" for staff to
 *    send by hand (click-to-chat) and mark as sent.
 *  - Automations only send messages and create tasks. They never change clinical records.
 */

export const CHANNELS = ["whatsapp", "sms", "email"] as const;
export type Channel = (typeof CHANNELS)[number];
export const PURPOSES = ["transactional", "reminders", "marketing"] as const;
export type Purpose = (typeof PURPOSES)[number];
export const TEMPLATE_CATEGORIES = ["transactional", "reminders", "marketing", "clinical_followup"] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

/** What a template category means for consent. Clinical follow-ups are service messages (reminders). */
export const PURPOSE_OF_CATEGORY: Record<TemplateCategory, Purpose> = {
  transactional: "transactional",
  reminders: "reminders",
  marketing: "marketing",
  clinical_followup: "reminders",
};

/** Every {{placeholder}} a template may use. Rendering fails (and the message is skipped) if one has no value. */
export const TEMPLATE_VARIABLES = {
  patient_name: "Patient's full name",
  patient_first_name: "Patient's first name",
  clinic_name: "Clinic name",
  clinic_phone: "Clinic phone number",
  branch_name: "Branch name",
  appointment_date: "Appointment date",
  appointment_time: "Appointment time",
  doctor_name: "Doctor's name",
  service_name: "Treatment or service name",
  review_link: "Review link (Settings → Reminders)",
  therapy_date: "Therapy day (therapy reminders)",
  therapy_time: "First therapy time that day",
  therapy_names: "Therapies that day",
} as const;
export type TemplateVariable = keyof typeof TEMPLATE_VARIABLES;

const PLACEHOLDER = /\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g;
/** Placeholders in order of first appearance. */
export function templateVariables(...parts: (string | null | undefined)[]): string[] {
  const seen: string[] = [];
  for (const p of parts) for (const m of (p ?? "").matchAll(PLACEHOLDER)) if (!seen.includes(m[1]!)) seen.push(m[1]!);
  return seen;
}

export class MissingVariableError extends Error {
  constructor(readonly variable: string) {
    super(`No value for {{${variable}}}`);
  }
}

/** Replaces {{placeholders}}; throws MissingVariableError when a value is missing or empty. */
export function renderTemplate(body: string, values: Partial<Record<string, string | null | undefined>>): string {
  return body.replace(PLACEHOLDER, (_m, name: string) => {
    const v = values[name];
    if (v === undefined || v === null || v === "") throw new MissingVariableError(name);
    return v;
  });
}

/* ------------------------------------------------------------------------------------- templates */

const unknownVariables = (s: string) => templateVariables(s).filter((v) => !(v in TEMPLATE_VARIABLES));

const templateContent = {
  subject: optionalText(200),
  body: z
    .string()
    .trim()
    .min(1)
    .max(1024)
    .refine((b) => unknownVariables(b).length === 0, { message: `Use only these placeholders: ${Object.keys(TEMPLATE_VARIABLES).map((v) => `{{${v}}}`).join(", ")}` }),
};

export const createTemplateInput = z
  .object({
    code: z.string().trim().regex(/^[a-z][a-z0-9_]{1,63}$/, "Lowercase letters, digits and _"),
    name: text(120),
    description: optionalText(500),
    channel: z.enum(CHANNELS),
    locale: z.string().trim().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, "e.g. en or ml").default("en"),
    category: z.enum(TEMPLATE_CATEGORIES),
    ...templateContent,
  })
  .refine((t) => t.channel !== "email" || !!t.subject, { message: "Email templates need a subject", path: ["subject"] });
export type CreateTemplateInput = z.infer<typeof createTemplateInput>;

export const updateTemplateInput = z.object({
  version: z.number().int().min(1),
  name: text(120),
  description: optionalText(500),
  ...templateContent,
  changeNote: optionalText(300),
  /** WhatsApp: the provider-approved template this wording matches (needed outside the 24-hour window). */
  providerTemplateName: z.string().trim().max(512).regex(/^[a-z0-9_]+$/, "Lowercase letters, digits and _ (as in WhatsApp Manager)").nullish().or(z.literal("")).transform((v) => v || null),
  providerLanguage: z.string().trim().regex(/^[a-z]{2,3}(_[A-Z]{2})?$/, "e.g. en or en_US").nullish().or(z.literal("")).transform((v) => v || null),
  providerStatus: z.enum(["pending", "approved", "rejected"]).nullish().transform((v) => v ?? null),
});
export const templateStatusInput = z.object({ version: z.number().int().min(1), status: z.enum(["active", "archived", "draft"]) });

export interface MessageTemplate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  channel: Channel;
  locale: string;
  category: TemplateCategory;
  status: "draft" | "active" | "archived";
  isSystem: boolean;
  providerTemplateName: string | null;
  providerLanguage: string | null;
  providerStatus: "pending" | "approved" | "rejected" | null;
  version: number;
  activeVersion: { id: string; versionNo: number; subject: string | null; body: string; variables: string[]; approvedBy: string | null; createdAt: string } | null;
  updatedAt: string;
}

/* --------------------------------------------------------------------------------------- consent */

export const setConsentInput = z.object({
  channel: z.enum(CHANNELS),
  purpose: z.enum(PURPOSES),
  optedIn: z.boolean(),
  note: optionalText(300),
});

export interface CommunicationConsent {
  /** Effective choice per channel and purpose (defaults applied). */
  current: Record<Channel, Record<Purpose, { optedIn: boolean; isDefault: boolean; changedAt: string | null; source: string | null }>>;
  history: { id: string; channel: Channel; purpose: Purpose; optedIn: boolean; source: string; sourceDetail: string | null; recordedBy: string | null; changedAt: string }[];
}

/* --------------------------------------------------------------------------------- notifications */

export const NOTIFICATION_STATUSES = ["pending", "processing", "manual", "sent", "delivered", "read", "failed", "skipped", "cancelled"] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];
export const NOTIFICATION_SOURCES = ["reminder", "recall", "follow_up", "workflow", "staff", "inbox", "system", "campaign"] as const;

export interface Notification {
  id: string;
  patient: { id: string; fullName: string; patientCode: string };
  channel: Channel;
  purpose: Purpose;
  source: (typeof NOTIFICATION_SOURCES)[number];
  status: NotificationStatus;
  statusReason: string | null;
  toAddress: string | null;
  renderedSubject: string | null;
  renderedBody: string;
  templateName: string | null;
  scheduledFor: string;
  sentAt: string | null;
  sentBy: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  createdAt: string;
  /** Click-to-chat link for sending by hand (WhatsApp, status manual). */
  manualLink: string | null;
}

export const notificationListQuery = z.object({
  status: z.enum([...NOTIFICATION_STATUSES, "upcoming", "problem"]).optional(),
  patientId: uuid.optional(),
  source: z.enum(NOTIFICATION_SOURCES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

/** A message to one patient now, from a template or typed by staff. */
export const sendMessageInput = z
  .object({
    patientId: uuid,
    channel: z.enum(CHANNELS),
    templateId: uuid.nullish().transform((v) => v ?? null),
    body: optionalText(1024),
    subject: optionalText(200),
  })
  .refine((m) => !!m.templateId !== !!m.body, { message: "Choose a template or type a message", path: ["body"] });

export const markManualSentInput = z.object({ note: optionalText(300) });
export const cancelNotificationInput = z.object({ reason: text(300) });

/* ---------------------------------------------------------------------------------------- inbox */

export interface Conversation {
  id: string;
  contactPhone: string;
  contactName: string | null;
  patient: { id: string; fullName: string; patientCode: string } | null;
  status: "open" | "closed";
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  unreadCount: number;
  lastMessagePreview: string | null;
  /** Free-text replies are allowed until this time (24 hours after the patient's last message). */
  windowOpenUntil: string | null;
}

export interface InboxMessage {
  id: string;
  direction: "inbound" | "outbound";
  type: string;
  body: string | null;
  status: string;
  sentBy: string | null;
  at: string;
}

export const replyInput = z.object({ body: text(1024) });

/* ----------------------------------------------------------------------------- channel accounts */

export const whatsappAccountInput = z.object({
  displayName: text(100),
  phoneNumberId: z.string().trim().regex(/^[0-9]{5,30}$/, "The numeric Phone number ID from Meta"),
  wabaId: z.string().trim().regex(/^[0-9]{5,30}$/, "The numeric WhatsApp Business Account ID").nullish().or(z.literal("")).transform((v) => v || null),
  displayPhoneNumber: z.string().trim().regex(/^\+[1-9][0-9]{6,14}$/, "e.g. +919847012345"),
  /** Where the access token lives: env://NAME on a single server, or a secret-manager URI. Never the token itself. */
  credentialsSecretRef: z
    .string()
    .trim()
    .regex(/^((vault|awssm|gcpsm|azkv):\/\/[A-Za-z0-9/_.:-]+|env:\/\/[A-Z][A-Z0-9_]{2,63})$/, "e.g. env://WHATSAPP_TOKEN_MAIN"),
  isDefault: z.boolean().default(true),
});

export interface ChannelAccount {
  id: string;
  channel: Channel;
  provider: string;
  displayName: string;
  phoneNumberId: string | null;
  wabaId: string | null;
  displayPhoneNumber: string | null;
  credentialsSecretRef: string;
  status: "pending" | "active" | "disabled" | "error";
  isDefault: boolean;
  lastError: string | null;
  lastHealthCheckAt: string | null;
}

/** What each channel can do right now (shown in Settings and next to "Send"). */
export interface ChannelStatus {
  whatsapp: { mode: "cloud_api" | "manual"; account: ChannelAccount | null; webhookConfigured: boolean };
  email: { configured: boolean };
  sms: { configured: false; reason: string };
  /** Set-up gaps that would make automatic messages be skipped (shown to staff before it happens). */
  problems: string[];
}

/* --------------------------------------------------------------------------------------- workflows */

/** Events a workflow can start from, with the payload fields its conditions can test. */
export const TRIGGER_EVENTS = {
  "appointment.booked": { label: "Appointment booked", fields: ["serviceId", "doctorId", "branchId"] },
  "appointment.confirmed": { label: "Appointment confirmed", fields: ["serviceId", "doctorId", "branchId"] },
  "appointment.completed": { label: "Appointment completed", fields: ["serviceId", "doctorId", "branchId"] },
  "appointment.no_show": { label: "Patient did not come (no-show)", fields: ["serviceId", "doctorId", "branchId"] },
  "appointment.cancelled": { label: "Appointment cancelled", fields: ["serviceId", "doctorId", "branchId"] },
  "treatment.completed": { label: "Procedure done in a visit", fields: ["serviceId"] },
  "patient.created": { label: "New patient registered", fields: [] },
  "invoice.issued": { label: "Invoice issued", fields: ["branchId"] },
  "lab_case.received": { label: "Lab work arrived at the clinic", fields: ["branchId", "doctorId", "restorationType"] },
} as const;
export type TriggerEvent = keyof typeof TRIGGER_EVENTS;

export const workflowConditionInput = z.object({
  fieldPath: z.string().regex(/^[a-z_][a-zA-Z0-9_]*(\.[a-z_][a-zA-Z0-9_]*)*$/),
  operator: z.enum(["eq", "neq", "in", "not_in", "exists"]),
  value: z.union([z.string(), z.array(z.string()).min(1).max(50)]).nullish().transform((v) => v ?? null),
});

export const sendMessageActionConfig = z.object({ templateCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/), channel: z.enum(CHANNELS) });
export const createTaskActionConfig = z.object({ title: text(200), priority: z.enum(["low", "normal", "high", "urgent"]).default("normal"), dueInMinutes: z.number().int().min(0).max(525_600).default(0) });

export const workflowActionInput = z.discriminatedUnion("actionType", [
  z.object({ actionType: z.literal("send_message"), delayMinutes: z.number().int().min(0).max(525_600).default(0), config: sendMessageActionConfig }),
  z.object({ actionType: z.literal("create_task"), delayMinutes: z.number().int().min(0).max(525_600).default(0), config: createTaskActionConfig }),
]);
export type WorkflowActionInput = z.infer<typeof workflowActionInput>;

export const workflowInput = z.object({
  name: text(120),
  description: optionalText(500),
  triggerEvent: z.enum(Object.keys(TRIGGER_EVENTS) as [TriggerEvent, ...TriggerEvent[]]),
  conditions: z.array(workflowConditionInput).max(10).default([]),
  actions: z.array(workflowActionInput).min(1).max(10),
});
export const updateWorkflowInput = workflowInput.extend({ version: z.number().int().min(1) });
export const workflowStatusInput = z.object({ version: z.number().int().min(1), status: z.enum(["active", "paused"]) });

export interface Workflow {
  id: string;
  name: string;
  description: string | null;
  triggerEvent: TriggerEvent;
  status: "draft" | "active" | "paused";
  isSystemTemplate: boolean;
  version: number;
  conditions: z.infer<typeof workflowConditionInput>[];
  actions: (WorkflowActionInput & { ordinal: number })[];
  stats: { runs: number; lastRunAt: string | null };
  updatedAt: string;
}

export interface WorkflowRun {
  id: string;
  workflowId: string;
  workflowName: string;
  sourceEventType: string;
  patient: { id: string; fullName: string } | null;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  lastError: string | null;
  steps: { ordinal: number; actionType: string; outcome: string; detail: string | null; at: string }[];
}

/* ------------------------------------------------------------------- reminders, recalls, follow-ups */

const channelList = z.array(z.enum(CHANNELS)).min(1).max(3).refine((a) => new Set(a).size === a.length, "Each channel once");

export const reminderPolicyInput = z.object({
  name: text(120),
  branchId: uuid.nullish().transform((v) => v ?? null),
  offsetsMinutes: z
    .array(z.number().int().min(15).max(43_200))
    .min(1)
    .max(5)
    .refine((a) => new Set(a).size === a.length, "Each time once"),
  channels: channelList,
  appliesToAllServices: z.boolean().default(true),
  serviceIds: z.array(uuid).max(200).default([]),
  templateCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/).default("appointment_reminder"),
  isActive: z.boolean().default(true),
});
export const updateReminderPolicyInput = reminderPolicyInput.extend({ version: z.number().int().min(1) });
export interface ReminderPolicy extends z.infer<typeof reminderPolicyInput> {
  id: string;
  version: number;
}

export const recallRuleInput = z.object({
  serviceId: uuid,
  name: text(120),
  intervalMonths: z.number().int().min(1).max(60),
  channels: channelList.default(["whatsapp"]),
  templateCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/).default("recall_due"),
  isActive: z.boolean().default(true),
});
export const updateRecallRuleInput = recallRuleInput.extend({ version: z.number().int().min(1) });
export interface RecallRule extends z.infer<typeof recallRuleInput> {
  id: string;
  serviceName: string;
  version: number;
}

export const RECALL_STATUSES = ["due", "notified", "booked", "dismissed"] as const;
export interface Recall {
  id: string;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  service: { id: string; name: string } | null;
  dueDate: string;
  status: (typeof RECALL_STATUSES)[number];
  notifiedAt: string | null;
  notificationCount: number;
  bookedAppointmentId: string | null;
  dismissedReason: string | null;
  notes: string | null;
  version: number;
}
export const recallListQuery = z.object({
  status: z.enum([...RECALL_STATUSES, "open"]).default("open"),
  dueBefore: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const createRecallInput = z.object({ patientId: uuid, serviceId: uuid.nullish().transform((v) => v ?? null), dueDate: isoDate, notes: optionalText(500) });
export const dismissRecallInput = z.object({ version: z.number().int().min(1), reason: text(300) });

export const followUpRuleInput = z.object({
  serviceId: uuid,
  name: text(120),
  offsetMinutes: z.number().int().min(30).max(43_200),
  channels: z.array(z.enum(CHANNELS)).max(3).default(["whatsapp"]),
  templateCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/).default("follow_up_check"),
  createTask: z.boolean().default(false),
  isActive: z.boolean().default(true),
}).refine((r) => r.channels.length > 0 || r.createTask, { message: "Send a message, create a call task, or both", path: ["channels"] });
export const updateFollowUpRuleInput = z.object({ version: z.number().int().min(1), rule: followUpRuleInput });
export interface FollowUpRule {
  id: string;
  serviceId: string;
  serviceName: string;
  name: string;
  offsetMinutes: number;
  channels: Channel[];
  templateCode: string;
  createTask: boolean;
  isActive: boolean;
  version: number;
}

export const FOLLOW_UP_OUTCOMES = ["doing_well", "needs_review", "appointment_booked", "unreachable", "other"] as const;
export interface FollowUp {
  id: string;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null };
  ruleName: string | null;
  serviceName: string | null;
  dueAt: string;
  status: "pending" | "sent" | "completed" | "cancelled";
  outcome: (typeof FOLLOW_UP_OUTCOMES)[number] | null;
  outcomeNotes: string | null;
  completedAt: string | null;
  version: number;
}
export const completeFollowUpInput = z.object({ version: z.number().int().min(1), outcome: z.enum(FOLLOW_UP_OUTCOMES), notes: optionalText(1000) });

/* ------------------------------------------------------------------------------------------- tasks */

export const TASK_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export const taskInput = z.object({
  title: text(200),
  description: optionalText(2000),
  patientId: uuid.nullish().transform((v) => v ?? null),
  assigneeUserId: uuid.nullish().transform((v) => v ?? null),
  priority: z.enum(TASK_PRIORITIES).default("normal"),
  dueAt: z.iso.datetime({ offset: true }).nullish().transform((v) => v ?? null),
});
export const updateTaskInput = taskInput.extend({ version: z.number().int().min(1) });
export const completeTaskInput = z.object({ version: z.number().int().min(1), status: z.enum(["done", "cancelled", "open"]), note: optionalText(1000) });
export const taskListQuery = z.object({
  status: z.enum(["open", "done", "cancelled"]).default("open"),
  mine: z.coerce.boolean().optional(),
  patientId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export interface Task {
  id: string;
  title: string;
  description: string | null;
  patient: { id: string; fullName: string; patientCode: string; phone: string | null } | null;
  assignee: { id: string; fullName: string } | null;
  priority: (typeof TASK_PRIORITIES)[number];
  status: "open" | "done" | "cancelled";
  dueAt: string | null;
  source: "staff" | "workflow" | "follow_up" | "system";
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  completedAt: string | null;
  completedBy: string | null;
  completionNote: string | null;
  createdAt: string;
  version: number;
}

/* ------------------------------------------------------------------------------------- worker health */

export interface EngagementHealth {
  queue: { queued: number; due: number; running: number; dead: number; oldestDueAt: string | null };
  outbox: { unpublished: number; oldestAt: string | null };
  notifications: { manual: number; failedLast7Days: number; sentLast7Days: number };
}
